<template>
  <div class="streaming-markdown">
    <!-- Rendered markdown content -->
    <div
      v-if="displayedHtml"
      ref="markdownContainer"
      class="streaming-markdown__content"
      v-html="displayedHtml"
    />
    <!-- Streaming text - simple span with CSS animation -->
    <span
      v-if="isStreaming && displayedPending"
      class="streaming-markdown__pending"
      >{{ displayedPending }}</span
    >
    <!-- Modern pulsing cursor -->
    <span v-if="isStreaming && showCursor" class="streaming-markdown__cursor" />
  </div>
</template>

<script>
import { marked } from 'marked';

// Configure marked for safe, clean output
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Strip vue-router `base` from a pathname so we can resolve/push internal paths.
 * @param {string} pathname
 * @param {string} base
 */
function stripBaseFromPathname(pathname, base) {
  if (!pathname) {
    return '/';
  }
  const b = (base || '/').replace(/\/$/, '');
  if (!b || b === '/') {
    return pathname;
  }
  if (pathname === b || pathname === `${b}/`) {
    return '/';
  }
  if (pathname.startsWith(`${b}/`) || pathname.startsWith(b)) {
    const rest = pathname.slice(b.length) || '/';
    return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return pathname;
}

export default {
  name: 'StreamingMarkdown',
  props: {
    /**
     * The full accumulated text content to render.
     */
    content: {
      type: [String, Array],
      default: '',
    },
    /**
     * Whether content is actively streaming.
     */
    streaming: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to show the cursor during streaming.
     */
    showCursor: {
      type: Boolean,
      default: true,
    },
    /**
     * Base speed for character reveal (ms per character).
     * Actual speed adapts based on backlog.
     */
    baseRevealSpeed: {
      type: Number,
      default: 8,
    },
  },
  data() {
    return {
      // What's actually displayed
      displayedHtml: '',
      displayedPending: '',
      // Target content we're revealing towards
      targetPending: '',
      // Animation state
      animationFrame: null,
      lastRevealTime: 0,
      // Track total content for markdown parsing
      lastParsedContent: '',
      // Debounce for markdown parsing only
      parseDebounce: null,
    };
  },
  computed: {
    normalizedContent() {
      if (Array.isArray(this.content)) {
        return this.content.join('');
      }
      return this.content || '';
    },
    isStreaming() {
      return this.streaming;
    },
  },
  watch: {
    content: {
      handler() {
        this.onContentChange();
      },
      deep: true,
      immediate: true,
    },
    streaming(isStreaming, wasStreaming) {
      if (wasStreaming && !isStreaming) {
        this.finishStreaming();
      }
    },
  },
  mounted() {
    this._onMarkdownLinkClick = this.onMarkdownLinkClick.bind(this);
    this.$el.addEventListener('click', this._onMarkdownLinkClick);

    if (this.normalizedContent) {
      this.onContentChange();
      if (!this.streaming) {
        this.renderFinal();
      }
    }
  },
  beforeDestroy() {
    if (this._onMarkdownLinkClick) {
      this.$el.removeEventListener('click', this._onMarkdownLinkClick);
    }
    this.cleanup();
  },
  methods: {
    /**
     * Same-origin links that resolve to a registered route use Vue Router (SPA navigation).
     */
    onMarkdownLinkClick(event) {
      const anchor = event.target.closest('a');
      if (!anchor || !anchor.getAttribute('href')) {
        return;
      }

      const container = this.$refs.markdownContainer;
      if (!container || !container.contains(anchor)) {
        return;
      }

      const rawHref = anchor.getAttribute('href');
      if (!rawHref || rawHref.trim().toLowerCase().startsWith('javascript:')) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) {
        return;
      }

      const router = this.$router;
      if (!router) {
        return;
      }

      const base = router.options.base || '/';
      const pathForRouter =
        stripBaseFromPathname(url.pathname, base) + url.search + url.hash;

      const resolved = router.resolve(pathForRouter);
      if (!resolved.route.matched.length) {
        return;
      }

      event.preventDefault();

      const dest =
        resolved.route.fullPath != null
          ? resolved.route.fullPath
          : pathForRouter;
      if (this.$route.fullPath === dest) {
        return;
      }

      router.push(resolved.location).catch(() => {});
    },

    /**
     * Handle content changes - immediately update targets, start animation.
     */
    onContentChange() {
      const text = this.normalizedContent;

      if (!text) {
        this.displayedHtml = '';
        this.displayedPending = '';
        this.targetPending = '';
        this.lastParsedContent = '';
        return;
      }

      if (!this.streaming) {
        this.renderFinal();
        return;
      }

      // Immediately calculate what to show
      this.updateTargets(text);

      // Start reveal animation if not running
      if (!this.animationFrame) {
        this.lastRevealTime = performance.now();
        this.animateReveal();
      }
    },

    /**
     * Split content into markdown-ready and pending portions.
     */
    updateTargets(text) {
      // Find the last newline to split content
      const lastNewline = text.lastIndexOf('\n');

      if (lastNewline === -1) {
        // No newlines - everything is pending, no markdown yet
        this.targetPending = text;
      } else {
        const completeText = text.substring(0, lastNewline + 1);
        const incompleteText = text.substring(lastNewline + 1);

        // Only re-parse markdown if the complete portion changed
        if (completeText !== this.lastParsedContent) {
          this.lastParsedContent = completeText;
          // Debounce the expensive markdown parsing
          this.queueMarkdownParse(completeText);
        }

        this.targetPending = incompleteText;
      }
    },

    /**
     * Debounce markdown parsing for performance.
     */
    queueMarkdownParse(text) {
      if (this.parseDebounce) {
        clearTimeout(this.parseDebounce);
      }

      // Very short debounce - just batches rapid sequential updates
      this.parseDebounce = setTimeout(() => {
        this.displayedHtml = marked.parse(text.trim());
      }, 8);
    },

    /**
     * Smooth reveal animation with adaptive speed.
     * Speeds up when there's a large backlog to prevent stalling.
     */
    animateReveal() {
      const now = performance.now();
      const elapsed = now - this.lastRevealTime;

      const targetLen = this.targetPending.length;
      const currentLen = this.displayedPending.length;
      const backlog = targetLen - currentLen;

      if (backlog > 0) {
        // Adaptive speed: speed up when backlog is large
        // Base: 8ms per char, but speed up proportionally to backlog
        let effectiveSpeed = this.baseRevealSpeed;
        if (backlog > 50) {
          // Large backlog - reveal much faster
          effectiveSpeed = Math.max(
            1,
            this.baseRevealSpeed / Math.ceil(backlog / 20)
          );
        } else if (backlog > 20) {
          // Medium backlog - reveal faster
          effectiveSpeed = Math.max(2, this.baseRevealSpeed / 2);
        }

        if (elapsed >= effectiveSpeed) {
          // Calculate how many chars to reveal this frame
          let charsToReveal = Math.floor(elapsed / effectiveSpeed);

          // For very large backlogs, reveal in word-sized chunks
          if (backlog > 100) {
            // Find next word boundary for smoother feel
            const remaining = this.targetPending.substring(currentLen);
            const nextSpace = remaining.indexOf(' ');
            if (nextSpace > 0 && nextSpace < 20) {
              charsToReveal = Math.max(charsToReveal, nextSpace + 1);
            } else {
              // No space soon, reveal larger chunk
              charsToReveal = Math.max(charsToReveal, Math.min(15, backlog));
            }
          }

          const newLen = Math.min(currentLen + charsToReveal, targetLen);
          this.displayedPending = this.targetPending.substring(0, newLen);
          this.lastRevealTime = now;
        }
      } else if (this.displayedPending !== this.targetPending) {
        // Content was reset (target is shorter or different)
        // Just sync immediately for clean transition
        this.displayedPending = this.targetPending;
      }

      // Continue animation loop
      this.animationFrame = requestAnimationFrame(() => this.animateReveal());
    },

    /**
     * Finish streaming - flush everything and render final markdown.
     */
    finishStreaming() {
      this.cleanup();
      // Small delay for visual smoothness
      setTimeout(() => {
        this.renderFinal();
      }, 30);
    },

    /**
     * Final render - convert everything to markdown.
     */
    renderFinal() {
      const text = this.normalizedContent.trim();
      if (!text) {
        this.displayedHtml = '';
        this.displayedPending = '';
        this.targetPending = '';
        return;
      }

      this.displayedHtml = marked.parse(text);
      this.displayedPending = '';
      this.targetPending = '';
    },

    /**
     * Clean up animation frames and timers.
     */
    cleanup() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      if (this.parseDebounce) {
        clearTimeout(this.parseDebounce);
        this.parseDebounce = null;
      }
    },
  },
};
</script>

<style lang="less">
.streaming-markdown {
  line-height: 1.6;

  // Pending text with subtle text rendering for smoothness
  &__pending {
    white-space: pre-wrap;
    font-family: inherit;
    display: inline;
    // Helps with text rendering during rapid updates
    text-rendering: optimizeSpeed;
    // Subtle opacity for "incoming" feel
    opacity: 0.95;
  }

  // Modern pulsing cursor (like Claude/ChatGPT)
  &__cursor {
    display: inline-block;
    width: 2px;
    height: 1.1em;
    background: currentColor;
    margin-left: 2px;
    vertical-align: text-bottom;
    border-radius: 1px;
    animation: streaming-cursor-pulse 1.2s ease-in-out infinite;
  }

  @keyframes streaming-cursor-pulse {
    0%,
    100% {
      opacity: 1;
      transform: scaleY(1);
    }
    50% {
      opacity: 0.3;
      transform: scaleY(0.85);
    }
  }

  &__content {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: 1em;
      margin-bottom: 0.5em;

      &:first-child {
        margin-top: 0;
      }
    }

    p {
      margin-bottom: 1em;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      margin-bottom: 1em;
      padding-left: 1.5em;
    }

    li {
      margin-bottom: 0.25em;
    }

    code {
      background-color: var(--v-lightGray-base, #f5f5f5);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: monospace;
    }

    pre {
      background-color: var(--v-lightGray-base, #f5f5f5);
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
      margin-bottom: 1em;

      code {
        background: none;
        padding: 0;
      }
    }

    blockquote {
      border-left: 4px solid var(--v-appMain-base, #68cdff);
      padding-left: 16px;
      margin: 1em 0;
      color: var(--v-darkGray-darken1, #666);
    }

    a {
      color: var(--v-primary-base, #1976d2);
      text-decoration: underline;
    }

    strong {
      font-weight: 600;
    }

    // GFM tables — chat panel is narrow; avoid horizontal overflow without scrollbars
    table {
      width: 100%;
      max-width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      margin: 0.75em 0 1em;
      font-size: 0.9375em;
    }

    thead th {
      font-weight: 600;
      text-align: left;
      background: var(--v-lightGray-base, #f5f5f5);
    }

    th,
    td {
      padding: 0.45em 0.5em;
      border: 1px solid rgba(0, 0, 0, 0.08);
      vertical-align: top;
      // Allow long tokens (IDs, emails) to wrap within the cell
      min-width: 0;
      word-break: break-word;
      overflow-wrap: anywhere;
      hyphens: auto;
    }

    // Inline code in tables (IDs) — break on any character if needed
    td code,
    th code {
      word-break: break-all;
      white-space: normal;
    }
  }
}
</style>
